import classNames from 'classnames';
import React, { memo, useCallback, useEffect, useRef } from 'react';

import { MessageOptions, MessageType } from '../Message/types';

import {
  CloseIcon,
  ErrorIcon,
  InfoIcon,
  LoadingIcon,
  SuccessIcon,
  WarningIcon,
} from './widgets/icons';

import './index.scss';

export interface INotice extends MessageOptions {
  id?: string | number;
  onRemove?: (id?: string | number) => void;
  scene?: 'display' | 'message';
  contentClassName?: string
}

/**
 * 获取消息类型对应的图标
 * @param type
 * @returns
 */
const getIcon = (type: MessageType) => {
  switch (type) {
    case 'notice':
      return <InfoIcon className="message-icon" />;
    case 'info':
      return <InfoIcon className="message-icon" />;
    case 'success':
      return <SuccessIcon className="message-icon" />;
    case 'error':
      return <ErrorIcon className="message-icon" />;
    case 'warning':
      return <WarningIcon className="message-icon" />;
    case 'loading':
      return <LoadingIcon className="message-icon" />;
    default:
      return null;
  }
};

const Notice: React.FC<INotice> = ({
  id,
  type = 'info',
  content,
  contentClassName,
  desc,
  duration = 3,
  onRemove,
  className,
  style,
  onClick,
  closable = false,
  actionText,
  onAction,
  onClose,
  scene = 'message',
}) => {
  const timerRef = useRef<number | null>(null);

  /**
   * 清除定时器
   */
  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  /**
   * 手动关闭通知
   */
  const close = useCallback(() => {
    clearTimer();
    onClose?.(id);
    onRemove?.(id);
  }, [id, onRemove, clearTimer]);

  /**
   * 点击Action
   */
  const handleAction = useCallback(
    (e: React.MouseEvent) => {
      onAction?.(e);
      close();
    },
    [onAction, close],
  );

  /**
   * 鼠标悬停时暂停计时器
   */
  const handleMouseEnter = useCallback(() => {
    clearTimer();
  }, [clearTimer]);

  /**
   * 鼠标离开时重新开始计时器
   */
  const handleMouseLeave = useCallback(() => {
    if (!closable && duration > 0) {
      timerRef.current = window.setTimeout(() => {
        close();
      }, duration * 1000);
    }
  }, [duration, close, closable]);

  /**
   * 组件挂载时设置定时器
   */
  useEffect(() => {
    if (!closable && duration > 0) {
      timerRef.current = window.setTimeout(() => {
        close();
      }, duration * 1000);
    }
    return clearTimer;
  }, [duration, close, clearTimer, closable]);

  return (
    <div
      className={classNames('odc-message-notice', className, {
        [`message-${type}`]: type,
        'has-desc': desc,
        'odc-message-notice-display': scene === 'display',
      })}
      style={style}
      onClick={onClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <div className="message-notice-content">
        <div className="message-icon-wrapper">{getIcon(type)}</div>
        <div className={classNames('message-content', contentClassName)}>{content}</div>
        <div className="message-actions">
          {actionText && (
            <button className="message-action-btn" onClick={handleAction}>
              {actionText}
            </button>
          )}
          {closable && (
            <button className="message-close-btn" onClick={close}>
              <CloseIcon className="message-close-icon" />
            </button>
          )}
        </div>
      </div>
      {desc && <div className="message-notice-desc">{desc}</div>}
    </div>
  );
};

export default memo(Notice);
